import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { KTSVG } from "../../../../_start/helpers";

interface Props {
  show: boolean;
  handleClose: () => void;
  projectTitle: string;
  projectDescription: string;
  onLogoGenerated?: (logoUrl: string) => void;
  generateLogo: (prompt: string, style: string) => Promise<{ logoUrl: string; prompt: string } | null>;
  isGenerating: boolean;
  error: string | null;
}

const logoStyles = [
  { id: "minimal", label: "Minimal", description: "Clean, simple, modern", icon: "bi-circle" },
  { id: "bold", label: "Bold", description: "Strong, impactful, memorable", icon: "bi-lightning" },
  { id: "elegant", label: "Elegant", description: "Sophisticated, refined, premium", icon: "bi-gem" },
  { id: "playful", label: "Playful", description: "Fun, friendly, approachable", icon: "bi-emoji-smile" },
  { id: "tech", label: "Tech", description: "Modern, innovative, digital", icon: "bi-cpu" },
  { id: "organic", label: "Organic", description: "Natural, flowing, handcrafted", icon: "bi-flower1" },
];

export const LogoGeneratorModal: React.FC<Props> = ({
  show,
  handleClose,
  projectTitle,
  projectDescription,
  onLogoGenerated,
  generateLogo,
  isGenerating,
  error,
}) => {
  const [selectedStyle, setSelectedStyle] = useState("minimal");
  const [additionalPrompt, setAdditionalPrompt] = useState("");
  const [generatedLogo, setGeneratedLogo] = useState<string | null>(null);
  const [usedPrompt, setUsedPrompt] = useState<string | null>(null);

  const handleGenerate = async () => {
    const fullPrompt = additionalPrompt.trim()
      ? `${projectDescription}. Additional requirements: ${additionalPrompt}`
      : projectDescription;

    const result = await generateLogo(fullPrompt, selectedStyle);
    if (result) {
      setGeneratedLogo(result.logoUrl);
      setUsedPrompt(result.prompt);
    }
  };

  const handleSave = () => {
    if (generatedLogo && onLogoGenerated) {
      onLogoGenerated(generatedLogo);
    }
    handleClose();
  };

  const handleRegenerate = () => {
    setGeneratedLogo(null);
    setUsedPrompt(null);
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      backdrop="static"
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bolder">
          <i className="bi bi-image me-2 text-primary"></i>
          Generate Logo for {projectTitle}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-3">
        {error && (
          <div className="alert alert-danger mb-4">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {!generatedLogo ? (
          <>
            {/* Style Selection */}
            <div className="mb-5">
              <label className="form-label fw-bold fs-6 mb-3">
                Choose a style
              </label>
              <div className="row g-3">
                {logoStyles.map((style) => (
                  <div key={style.id} className="col-md-4 col-sm-6 d-flex">
                    <label
                      className={`d-flex align-items-center p-3 rounded cursor-pointer border w-100 ${
                        selectedStyle === style.id
                          ? "border-primary bg-light-primary"
                          : "border-gray-300 bg-hover-light"
                      }`}
                      style={{ transition: "all 0.2s ease" }}
                    >
                      <input
                        type="radio"
                        name="logoStyle"
                        value={style.id}
                        checked={selectedStyle === style.id}
                        onChange={(e) => setSelectedStyle(e.target.value)}
                        className="form-check-input me-3"
                      />
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center">
                          <i className={`bi ${style.icon} fs-4 me-2 text-primary`}></i>
                          <span className="fw-bold">{style.label}</span>
                        </div>
                        <span className="text-muted fs-7">{style.description}</span>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Business Context */}
            <div className="mb-5">
              <label className="form-label fw-bold fs-6 mb-2">
                Business context
              </label>
              <div className="bg-light rounded p-3">
                <p className="text-gray-700 mb-0 fs-7">
                  {projectDescription || "No description provided. Add a project description for better results."}
                </p>
              </div>
            </div>

            {/* Additional Requirements */}
            <div className="mb-5">
              <label className="form-label fw-bold fs-6 mb-2">
                Additional requirements (optional)
              </label>
              <textarea
                className="form-control form-control-solid"
                rows={3}
                placeholder="E.g., 'Use blue and green colors', 'Include a mountain symbol', 'Make it suitable for a mobile app'"
                value={additionalPrompt}
                onChange={(e) => setAdditionalPrompt(e.target.value)}
              />
            </div>
          </>
        ) : (
          <>
            {/* Generated Logo Display */}
            <div className="text-center mb-5">
              <div className="d-inline-block p-5 bg-white rounded shadow-sm border">
                <img
                  src={generatedLogo}
                  alt="Generated Logo"
                  style={{ maxWidth: "300px", maxHeight: "300px" }}
                  className="img-fluid"
                />
              </div>
            </div>

            {/* Prompt Used */}
            {usedPrompt && (
              <div className="mb-4">
                <label className="form-label fw-bold fs-7 text-muted mb-1">
                  Generated using this prompt:
                </label>
                <div className="bg-light rounded p-3">
                  <p className="text-gray-600 mb-0 fs-8 fst-italic">{usedPrompt}</p>
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <button
          type="button"
          className="btn btn-light"
          onClick={handleClose}
          disabled={isGenerating}
        >
          Cancel
        </button>

        {!generatedLogo ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleGenerate}
            disabled={isGenerating || !projectDescription}
          >
            {isGenerating ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Generating...
              </>
            ) : (
              <>
                <i className="bi bi-magic me-2"></i>
                Generate Logo
              </>
            )}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-light-primary"
              onClick={handleRegenerate}
              disabled={isGenerating}
            >
              <i className="bi bi-arrow-clockwise me-2"></i>
              Regenerate
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
            >
              <i className="bi bi-check-lg me-2"></i>
              Save Logo
            </button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};
